import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle, XCircle, Palette } from "@phosphor-icons/react";
import { portalApi } from "@/lib/portalApi";
import { formatApiErrorDetail } from "@/lib/api";

const money = (n) => `$${Math.abs(Number(n || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const PortalColourOptions = ({ quoteId, closed }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    portalApi.get(`/quotes/${quoteId}/colour-options`).then(({ data }) => setData(data)).catch(() => setData({ areas: [] }));
  }, [quoteId]);

  if (!data || !data.areas?.length) return null;

  const decide = async (area, choice, decision) => {
    const key = `${area.area_id}:${choice.id}`;
    setBusy(key);
    try {
      const { data: next } = await portalApi.post(`/quotes/${quoteId}/colour-options/decide`, {
        area_id: area.area_id, choice_id: choice.id, decision,
      });
      setData(next);
      if (decision === "accept") toast.success(`${choice.slab_color} chosen for ${area.name}`);
      else if (decision === "decline") toast.success(`${choice.slab_color} declined`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save your choice");
    }
    setBusy("");
  };

  const locked = closed || data.closed;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="portal-colour-options">
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-100 text-amber-700">
          <Palette size={16} weight="bold" />
        </span>
        <div>
          <h2 className="text-base font-semibold text-slate-900 md:text-lg">Choose your colors</h2>
          <p className="text-xs text-slate-500">Accept the color you want for each area, or decline the ones you don't. We'll update your quote.</p>
        </div>
      </div>
      <div className="mt-4 space-y-5">
        {data.areas.map((area) => (
          <div key={area.area_id} data-testid={`portal-colour-area-${area.area_id}`}>
            <div className="mb-2 flex items-baseline gap-2">
              <span className="text-sm font-semibold text-slate-800">{area.name}</span>
              <span className="text-xs text-slate-500">{area.sqft} ft²</span>
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {area.choices.map((c) => {
                const st = c.decision;
                const key = `${area.area_id}:${c.id}`;
                const photo = c.photo ? `${process.env.REACT_APP_BACKEND_URL}/api/portal/quotes/${quoteId}/colour-options/photo?color=${encodeURIComponent(c.slab_color)}` : "";
                return (
                  <div
                    key={c.id}
                    data-testid={`portal-colour-choice-${area.area_id}-${c.id}`}
                    data-decision={st}
                    className={`overflow-hidden rounded-xl border transition-colors ${
                      st === "accept" ? "border-emerald-400 ring-2 ring-emerald-200" : st === "decline" ? "border-slate-200 opacity-60" : "border-slate-200"
                    }`}
                  >
                    {(() => {
                      const media = photo ? (
                        <img src={photo} alt={c.slab_color} className="h-28 w-full object-cover" />
                      ) : (
                        <div className="h-28 w-full bg-gradient-to-br from-slate-100 to-slate-200" />
                      );
                      return c.product_url ? (
                        <a
                          href={c.product_url}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`portal-colour-link-${area.area_id}-${c.id}`}
                          title={`View ${c.slab_color}`}
                          className="block cursor-pointer"
                        >
                          {media}
                        </a>
                      ) : (
                        media
                      );
                    })()}
                    {(c.recommended || c.best_value || c.premium) && (
                      <div
                        data-testid={`portal-colour-ribbon-${area.area_id}-${c.id}`}
                        className={`px-3 pt-2 text-[10px] font-bold uppercase tracking-widest ${
                          c.premium && !c.recommended && !c.best_value ? "text-amber-700" : "text-emerald-700"
                        }`}
                      >
                        ★ {c.recommended ? "Our recommendation" : c.best_value ? "Best value" : "Premium"}
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-slate-900">
                            {c.product_url ? (
                              <a
                                href={c.product_url}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`portal-colour-name-${area.area_id}-${c.id}`}
                                className="hover:text-emerald-700 hover:underline"
                              >
                                {c.slab_color}
                              </a>
                            ) : (
                              c.slab_color
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {c.current ? "Quoted color" : Math.abs(c.delta) < 0.005 ? "Same price" : `${c.delta > 0 ? "+" : "−"}${money(c.delta)} ${c.delta > 0 ? "more" : "less"}`}
                          </div>
                          {c.note && <div className="mt-1 text-xs text-slate-500">{c.note}</div>}
                        </div>
                        {st === "accept" && <CheckCircle size={20} weight="fill" className="shrink-0 text-emerald-600" />}
                        {st === "decline" && <XCircle size={20} weight="fill" className="shrink-0 text-slate-400" />}
                      </div>
                      {!locked && (
                        <div className="mt-3 flex gap-2">
                          <button
                            type="button"
                            data-testid={`portal-colour-accept-${area.area_id}-${c.id}`}
                            disabled={busy === key}
                            onClick={() => decide(area, c, st === "accept" ? "clear" : "accept")}
                            className={`h-9 flex-1 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-colors ${
                              st === "accept" ? "bg-emerald-600 text-white" : "border border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            } disabled:opacity-40`}
                          >
                            {st === "accept" ? "Chosen" : "Accept"}
                          </button>
                          <button
                            type="button"
                            data-testid={`portal-colour-decline-${area.area_id}-${c.id}`}
                            disabled={busy === key}
                            onClick={() => decide(area, c, st === "decline" ? "clear" : "decline")}
                            className={`h-9 flex-1 rounded-lg text-[11px] font-bold uppercase tracking-widest transition-colors ${
                              st === "decline" ? "bg-slate-700 text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-100"
                            } disabled:opacity-40`}
                          >
                            {st === "decline" ? "Declined" : "Decline"}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
